// One bad block must not blank the page.
//
// A page model is authored by a generator, an agent or by hand, and it drifts:
// a `feature_grid` whose items were written as `features`, a `button` whose
// spec was written flat. Every renderer reads its block's fields directly, so
// a missing list throws out of the whole React tree and the visitor gets a
// white screen with `Cannot read properties of undefined` in the console —
// for a page whose other twelve blocks were fine. React unwinds to the
// nearest boundary, so without one per block the nearest is the page.
//
// This is the ONE implementation, shared by every surface that renders a
// block: the designer canvas, the in-app smart-page preview and the deployed
// runtime client. It used to live privately in the designer's BlockNode,
// which is why the two runtime surfaces had no net at all.
//
// `tone` is the whole difference between them. The canvas and the preview are
// looked at by the AUTHOR, who can fix the block and needs the message; the
// deployed app is looked at by a VISITOR, who can do nothing with a stack
// trace, so they get a short note (and the console keeps the detail). Neither
// renders nothing: a page that silently drops a block teaches its author that
// the block is fine.
//
// Renderers still guard their own list reads — a missing array should render
// empty, not an error card. This is the net under them, for the mistake
// nobody anticipated.

import React from 'react'

export type BlockErrorTone = 'author' | 'visitor'

interface Props {
  blockId: string
  blockType: string
  /** 'author' (default) names the block and the error; 'visitor' is a note. */
  tone?: BlockErrorTone
  children: React.ReactNode
}

interface State {
  error: Error | null
}

export class BlockErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // Always the console, whatever the tone — this is the detail whoever ends
    // up debugging needs, and it is the only copy on a visitor surface.
    console.error(
      `[block ${this.props.blockId} (${this.props.blockType})] render failed:`,
      error, info.componentStack,
    )
  }

  componentDidUpdate(prev: Props): void {
    // A different block, or the same one re-authored into a different type,
    // gets a fresh attempt — so fixing it in the designer re-renders rather
    // than leaving the placeholder until a full reload.
    if (this.state.error
        && (prev.blockId !== this.props.blockId || prev.blockType !== this.props.blockType)) {
      this.setState({ error: null })
    }
  }

  render(): React.ReactNode {
    const { error } = this.state
    if (!error) return this.props.children
    if (this.props.tone === 'visitor') {
      return (
        <div
          role="note"
          style={{
            padding: '10px 12px', borderRadius: 8, fontSize: '0.8625rem', lineHeight: 1.45,
            border: '1px dashed var(--pg-border, #d4d4d8)',
            background: 'var(--pg-surface-alt, var(--pg-surface, transparent))',
            color: 'var(--pg-text-secondary, #71717a)',
          }}
        >
          This section could not be displayed.
        </div>
      )
    }
    return (
      <BlockErrorPlaceholder
        title={`Block "${this.props.blockId}" (${this.props.blockType}) failed to render`}
        hint={error.message || 'Unexpected error'}
        detail="Open the property panel to inspect this block, or delete it if it's beyond repair."
      />
    )
  }
}

export function BlockErrorPlaceholder({
  title, hint, detail,
}: { title: string; hint?: string; detail?: string }) {
  return (
    <div
      role="alert"
      style={{
        display: 'flex', gap: 10, alignItems: 'flex-start', padding: 12,
        border: '1px dashed #f59e0b', background: 'rgba(245, 158, 11, 0.08)',
        borderRadius: 6, fontSize: 14, color: 'var(--pg-text, #1e293b)',
      }}
    >
      {/* Inline glyph rather than a lucide import: this file is shared with
          the deployed runtime, and a warning triangle is not worth a dep. */}
      <span aria-hidden style={{ flexShrink: 0, marginTop: 1, color: '#b45309', fontSize: 16, lineHeight: '15px' }}>▲</span>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontWeight: 600 }}>{title}</span>
        {hint && (
          <span style={{ color: 'var(--pg-text-secondary, #64748b)', fontFamily: 'monospace', fontSize: 13 }}>
            {hint}
          </span>
        )}
        {detail && <span style={{ color: 'var(--pg-text-secondary, #64748b)' }}>{detail}</span>}
      </div>
    </div>
  )
}

export default BlockErrorBoundary
